import { useQuery, useMutation } from "@tanstack/react-query";
import type { UseQueryOptions, UseMutationOptions } from "@tanstack/react-query";
import { customFetch } from "./custom-fetch";
import type { ErrorType } from "./custom-fetch";

export interface CustomerAttachment {
  id: number;
  userId: number;
  customerId: number;
  fileName: string;
  contentType: string;
  objectPath: string;
  createdAt: string;
}

export interface CreateCustomerAttachmentBody {
  fileName: string;
  contentType: string;
  objectPath: string;
}

export const getListCustomerAttachmentsQueryKey = (customerId: number) => {
  return [`/api/customers/${customerId}/attachments`] as const;
};

export const listCustomerAttachments = async (
  customerId: number
): Promise<CustomerAttachment[]> => {
  return customFetch<CustomerAttachment[]>(
    `/api/customers/${customerId}/attachments`
  );
};

export const useListCustomerAttachments = <
  TData = CustomerAttachment[],
  TError = ErrorType<unknown>
>(
  customerId: number,
  options?: {
    query?: UseQueryOptions<CustomerAttachment[], TError, TData>;
  }
) => {
  const { query: queryOptions } = options ?? {};
  const queryKey =
    queryOptions?.queryKey ?? getListCustomerAttachmentsQueryKey(customerId);

  return useQuery<CustomerAttachment[], TError, TData>({
    queryKey,
    queryFn: () => listCustomerAttachments(customerId),
    enabled: !!customerId,
    ...queryOptions,
  });
};

export const createCustomerAttachment = async (
  customerId: number,
  data: CreateCustomerAttachmentBody
): Promise<CustomerAttachment> => {
  return customFetch<CustomerAttachment>(
    `/api/customers/${customerId}/attachments`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    }
  );
};

export const useCreateCustomerAttachment = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    CustomerAttachment,
    TError,
    { customerId: number; data: CreateCustomerAttachmentBody },
    TContext
  >
) => {
  return useMutation<
    CustomerAttachment,
    TError,
    { customerId: number; data: CreateCustomerAttachmentBody },
    TContext
  >({
    mutationFn: ({ customerId, data }) =>
      createCustomerAttachment(customerId, data),
    ...options,
  });
};

export const deleteCustomerAttachment = async (
  customerId: number,
  attachmentId: number
): Promise<void> => {
  return customFetch<void>(
    `/api/customers/${customerId}/attachments/${attachmentId}`,
    { method: "DELETE" }
  );
};

export const useDeleteCustomerAttachment = <
  TError = ErrorType<unknown>,
  TContext = unknown
>(
  options?: UseMutationOptions<
    void,
    TError,
    { customerId: number; attachmentId: number },
    TContext
  >
) => {
  return useMutation<
    void,
    TError,
    { customerId: number; attachmentId: number },
    TContext
  >({
    mutationFn: ({ customerId, attachmentId }) =>
      deleteCustomerAttachment(customerId, attachmentId),
    ...options,
  });
};
